import {
  FoodBookingModel,
  ServiceBookingModel,
  ViewEmployee,
  ViewRoom,
  ViewTable,
} from '@xeetr/types/models'

import {
  BookingFormValues,
  FoodBookingFormValues,
  ServiceBookingFormValues,
} from '../types/BookingForm'

type WithFoodRefs = Pick<FoodBookingModel, 'room' | 'tables'>
type WithServiceRefs = Pick<ServiceBookingModel, 'employee'>

export const toFoodBookingFormValues = <T extends WithFoodRefs>({ room, tables, ...rest }: T) => ({
  ...rest,
  roomId: room?.id,
  tableIds: (tables ?? []).map((table) => table.id),
})

export const toServiceBookingFormValues = <T extends WithServiceRefs>({
  employee,
  ...rest
}: T) => ({
  ...rest,
  employeeId: employee?.id,
})

/** Maps an API booking (food bookings always carry `tables`) to form values. */
export const toBookingFormValues = (
  booking: FoodBookingModel | ServiceBookingModel
): BookingFormValues =>
  'tables' in booking
    ? toFoodBookingFormValues(booking)
    : toServiceBookingFormValues(booking as ServiceBookingModel)

const toDate = (value: string | Date | undefined | null): Date | undefined => {
  if (!value) return undefined
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) {
    console.warn('Invalid date provided:', value)
    return undefined
  }
  return date
}

/** Form values for the edit form: `from`/`until` arrive as ISO strings from the API but the form holds Dates. */
export const toEditFormValues = (
  booking: FoodBookingModel | ServiceBookingModel
): BookingFormValues =>
  ({
    ...toBookingFormValues(booking),
    from: toDate(booking.from),
    until: toDate(booking.until),
    // Keys missing here keep react-hook-form's isDirty true after a reset
    bookingAdditions: {
      note: undefined,
      additionalInfo: undefined,
      ...booking.bookingAdditions,
    },
  }) as BookingFormValues

export const isFoodBookingFormValues = (
  values: BookingFormValues
): values is FoodBookingFormValues => 'tableIds' in values

// The API only reads `id` from room, tables and employee and re-reads the saved booking
// from the DB, so id stubs are sufficient (the series endpoint already sends stubs).
export const fromFoodBookingFormValues = ({
  roomId,
  tableIds,
  ...rest
}: FoodBookingFormValues): FoodBookingModel => ({
  ...rest,
  room: roomId ? ({ id: roomId } as ViewRoom) : undefined,
  tables: (tableIds ?? []).map((id) => ({ id }) as ViewTable),
})

export const fromServiceBookingFormValues = ({
  employeeId,
  ...rest
}: ServiceBookingFormValues): ServiceBookingModel => ({
  ...rest,
  employee: employeeId ? ({ id: employeeId } as ViewEmployee) : undefined,
})

export const fromBookingFormValues = (
  values: BookingFormValues
): FoodBookingModel | ServiceBookingModel =>
  isFoodBookingFormValues(values)
    ? fromFoodBookingFormValues(values)
    : fromServiceBookingFormValues(values)
